import {
  Box,
  Button,
  IconButton,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CustomItem,
  LocalizedString,
  ScreenAnchor,
  SchemeContent,
  SchemeKnobValues,
  SchemeManifest,
  SchemeToggleKnob,
} from "@shared/types/foyer";
import { CUSTOM_ITEM_MAX, SCREEN_ANCHORS } from "@shared/types/foyer";
import ColorSwatch from "../ColorSwatch";
import LocalizedTextField from "../LocalizedTextField";
import AssetField from "../AssetField";
import { resolveLocalized } from "@shared/i18n";
import { tokens } from "../../theme";
import { ItemFormSurface } from "../EditDrawer";
import EditorCard from "./EditorCard";
import { EDITOR_MAX_WIDTH, fieldGrid } from "./editorLayout";

interface Props {
  manifest: SchemeManifest;
  /** Knob values - the screen's NAME lives here, not in content. */
  knobs: SchemeKnobValues | undefined;
  onKnobs: (next: SchemeKnobValues) => void;
  content: SchemeContent;
  onChange: (next: SchemeContent) => void;
  supportedLocales: string[];
  defaultLocale: string;
}

const URL_PLACEHOLDER = "https://…";
const SEPARATOR = " · ";

/** Defaults for a brand-new item: readable over any photograph on day one. */
const NEW_ITEM = (n: number): CustomItem => ({
  id: `c${n}-${Math.random().toString(36).slice(2, 8)}`,
  label: `Item ${n}`,
  vignette: { size: 55, color: "#000000" },
  text: {
    body: "",
    placement: "middle-left",
    fontSize: 64,
    color: "#ffffff",
  },
  qr: { url: "", placement: "bottom-right" },
});

/**
 * The Custom screen's editor: the one pane where the operator designs rather
 * than fills in.
 *
 * Each item is one card, because on the TV each item IS one composition - its
 * own picture, vignette, words and QR, swapped in as the resident moves along
 * the bottom bar. Authoring them as four little pages rather than one page with
 * four backgrounds is what keeps the editor honest about what the resident sees.
 */
export default function CustomScreenEditor({
  manifest,
  knobs,
  onKnobs,
  content,
  onChange,
  supportedLocales,
  defaultLocale,
}: Props) {
  const { t } = useTranslation(["templates", "common"]);
  const loc = { supportedLocales, defaultLocale };
  const toggle = manifest.knobs.find(
    (k): k is SchemeToggleKnob => k.type === "toggle" && !!k.titleKnobId
  );
  const titleKnob = manifest.knobs.find(
    (k) => k.id === toggle?.titleKnobId && k.type === "text"
  );
  const rawTitle =
    titleKnob && typeof knobs?.[titleKnob.id] === "string"
      ? (knobs[titleKnob.id] as string)
      : "";

  const items = content.custom?.items ?? [];
  const setItems = (next: CustomItem[]) =>
    onChange({ ...content, custom: { ...content.custom, items: next } });
  // Which item the modal / drawer is on: "new" = adding one.
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const editingItem =
    editing === "new"
      ? NEW_ITEM(items.length + 1)
      : editing !== null
        ? items[editing]
        : undefined;
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
      {titleKnob && titleKnob.type === "text" && (
        <EditorCard
          eyebrow={t("common:labels.name")}
          data-testid="custom-screen-name"
          hint={t("customScreenEditor.nameHint")}
        >
          <TextField
            size="small"
            label={titleKnob.label}
            placeholder={titleKnob.placeholder ?? titleKnob.default}
            value={rawTitle}
            onChange={(e) =>
              onKnobs({ ...(knobs ?? {}), [titleKnob.id]: e.target.value })
            }
            inputProps={{
              maxLength: titleKnob.maxLength,
              "aria-label": titleKnob.label,
            }}
            data-testid="custom-title-field"
            sx={{ maxWidth: 320 }}
          />
        </EditorCard>
      )}

      <EditorCard
        eyebrow={t("customScreenEditor.itemsEyebrow", {
          current: items.length,
          max: CUSTOM_ITEM_MAX,
        })}
        data-testid="custom-items"
        hint={t("customScreenEditor.itemsHint")}
        action={
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={items.length >= CUSTOM_ITEM_MAX}
            onClick={() => setEditing("new")}
            data-testid="custom-add-item"
          >
            {t("customScreenEditor.addItem")}
          </Button>
        }
      >
        {items.length === 0 ? (
          <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
            {t("customScreenEditor.noItems")}
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {items.map((item, i) => (
              <ItemRow
                key={item.id}
                item={item}
                index={i}
                count={items.length}
                defaultLocale={defaultLocale}
                onEdit={() => setEditing(i)}
                onMove={(dir) => move(i, dir)}
                onRemove={() => setItems(items.filter((_, n) => n !== i))}
              />
            ))}
          </Stack>
        )}
      </EditorCard>

      {editing !== null && editingItem && (
        <ItemSurface
          key={editing === "new" ? "new" : editingItem.id}
          mode={editing === "new" ? "create" : "edit"}
          initial={editingItem}
          index={editing === "new" ? items.length : editing}
          loc={loc}
          onClose={() => setEditing(null)}
          onSave={(next) => {
            setItems(
              editing === "new"
                ? [...items, next]
                : items.map((it, n) => (n === editing ? next : it))
            );
            setEditing(null);
          }}
        />
      )}
    </Stack>
  );
}

/** One item in the list: its picture, its label, reorder, edit and remove. */
function ItemRow({
  item,
  index,
  count,
  defaultLocale,
  onEdit,
  onMove,
  onRemove,
}: {
  item: CustomItem;
  index: number;
  count: number;
  defaultLocale: string;
  onEdit: () => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const n = index + 1;
  const label =
    resolveLocalized(item.label, defaultLocale, defaultLocale).trim() ||
    t("customScreenEditor.itemFallback", { n });
  const body = resolveLocalized(
    item.text?.body,
    defaultLocale,
    defaultLocale
  ).trim();
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      data-testid={`custom-item-${index}`}
      sx={{
        border: "1px solid",
        borderColor: tokens.divider,
        borderRadius: "14px",
        px: 1.5,
        py: 1,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label={t("customScreenEditor.editItem", { n })}
        onClick={onEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit();
          }
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 64,
            height: 36,
            flexShrink: 0,
            borderRadius: "6px",
            bgcolor: tokens.rowAlt,
            backgroundImage: item.imageUrl ? `url(${item.imageUrl})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
          >
            {label}
          </Typography>
          <Typography variant="meta" noWrap sx={{ color: tokens.inkFaint }}>
            {[
              body || t("customScreenEditor.noText"),
              item.qr?.url ? t("customScreenEditor.qrBadge") : "",
            ]
              .filter(Boolean)
              .join(SEPARATOR)}
          </Typography>
        </Box>
      </Box>
      <Tooltip title={t("common:buttons.edit")}>
        <IconButton
          size="small"
          onClick={onEdit}
          data-testid={`custom-edit-${index}`}
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("customScreenEditor.moveLeft")}>
        <span>
          <IconButton
            size="small"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label={t("customScreenEditor.moveItemLeft", { n })}
          >
            <ArrowUpwardIcon fontSize="inherit" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t("customScreenEditor.moveRight")}>
        <span>
          <IconButton
            size="small"
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            aria-label={t("customScreenEditor.moveItemRight", { n })}
          >
            <ArrowDownwardIcon fontSize="inherit" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t("common:buttons.remove")}>
        <IconButton
          size="small"
          onClick={onRemove}
          aria-label={t("customScreenEditor.removeItem", { n })}
          data-testid={`custom-remove-${index}`}
        >
          <DeleteOutlineIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

/**
 * Add (modal) / edit (right drawer) one item. It works on a local copy: Add /
 * Apply hands it to `onSave` - which writes it into the draft the page saves
 * later - once the surface has closed; Cancel throws it away.
 */
function ItemSurface({
  mode,
  initial,
  index,
  loc,
  onClose,
  onSave,
}: {
  mode: "create" | "edit";
  initial: CustomItem;
  index: number;
  loc: { supportedLocales: string[]; defaultLocale: string };
  onClose: () => void;
  onSave: (next: CustomItem) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const [draft, setDraft] = useState<CustomItem>(initial);
  return (
    <ItemFormSurface
      mode={mode}
      onClose={onClose}
      title={
        mode === "create"
          ? t("customScreenEditor.addItem")
          : t("customScreenEditor.editItem", { n: index + 1 })
      }
      subtitle={t("customScreenEditor.itemSubtitle")}
      maxWidth="md"
      drawerWidth={900}
      data-testid="custom-item-form"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => dismiss(() => onSave(draft))}
            data-testid="custom-item-save"
          >
            {mode === "create"
              ? t("common:buttons.add")
              : t("common:buttons.apply")}
          </Button>
        </>
      )}
    >
      <ItemFields
        item={draft}
        index={index}
        loc={loc}
        onPatch={(part) => setDraft((d) => ({ ...d, ...part }))}
      />
    </ItemFormSurface>
  );
}

interface ItemProps {
  item: CustomItem;
  index: number;
  loc: { supportedLocales: string[]; defaultLocale: string };
  onPatch: (part: Partial<CustomItem>) => void;
}

/** The fields of one item, inside the Add modal or the Edit drawer. */
function ItemFields({ item, index, loc, onPatch }: ItemProps) {
  const { t } = useTranslation("templates");
  const n = index + 1;
  const text = item.text ?? {
    body: "" as LocalizedString,
    placement: "middle-left" as ScreenAnchor,
    fontSize: 64,
    color: "#ffffff",
  };
  const qr = item.qr ?? { url: "", placement: "bottom-right" as ScreenAnchor };
  const vignette = item.vignette ?? { size: 55, color: "#000000" };

  return (
    <Box data-testid={`custom-item-fields-${index}`} sx={{ pt: 1 }}>
      <Stack spacing={2.5}>
        <Box sx={fieldGrid(300)}>
          <LocalizedTextField
            label={t("customScreenEditor.menuLabel")}
            value={item.label}
            {...loc}
            onChange={(v) => onPatch({ label: v })}
            helperText={t("customScreenEditor.menuLabelHelp")}
            testId={`custom-label-${index}`}
          />
          <AssetField
            label={t("customScreenEditor.backgroundPicture")}
            value={item.imageUrl ?? ""}
            preview
            onChange={(v) => onPatch({ imageUrl: v || undefined })}
            testId={`custom-image-${index}`}
          />
        </Box>

        <Section
          title={t("customScreenEditor.vignette")}
          hint={t("customScreenEditor.vignetteHint")}
        >
          <Box sx={fieldGrid(280)}>
            <Box>
              <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                {t("customScreenEditor.vignetteSize", { size: vignette.size })}
              </Typography>
              <Slider
                size="small"
                value={vignette.size}
                min={0}
                max={100}
                onChange={(_, v) =>
                  onPatch({ vignette: { ...vignette, size: v as number } })
                }
                aria-label={t("customScreenEditor.vignetteSizeFor", { n })}
                data-testid={`custom-vignette-size-${index}`}
              />
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ColorSwatch
                value={vignette.color}
                onChange={(v) =>
                  onPatch({ vignette: { ...vignette, color: v } })
                }
                ariaLabel={t("customScreenEditor.vignetteColourFor", { n })}
              />
              <TextField
                size="small"
                label={t("customScreenEditor.vignetteColour")}
                value={vignette.color}
                onChange={(e) =>
                  onPatch({ vignette: { ...vignette, color: e.target.value } })
                }
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>
        </Section>

        <Section title={t("customScreenEditor.text")}>
          <Stack spacing={2}>
            <LocalizedTextField
              label={t("customScreenEditor.text")}
              value={text.body}
              multiline
              rows={3}
              {...loc}
              onChange={(v) => onPatch({ text: { ...text, body: v } })}
              testId={`custom-text-${index}`}
            />
            <Box sx={fieldGrid(240)}>
              <AnchorPicker
                label={t("customScreenEditor.placement")}
                value={text.placement}
                onChange={(placement) =>
                  onPatch({ text: { ...text, placement } })
                }
                testId={`custom-text-anchor-${index}`}
              />
              <Box>
                <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                  {t("customScreenEditor.fontSize", { size: text.fontSize })}
                </Typography>
                <Slider
                  size="small"
                  value={text.fontSize}
                  min={24}
                  max={160}
                  step={2}
                  onChange={(_, v) =>
                    onPatch({ text: { ...text, fontSize: v as number } })
                  }
                  aria-label={t("customScreenEditor.fontSizeFor", { n })}
                  data-testid={`custom-font-size-${index}`}
                />
                <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
                  {t("customScreenEditor.fontSizeHelp")}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ColorSwatch
                  value={text.color}
                  onChange={(v) => onPatch({ text: { ...text, color: v } })}
                  ariaLabel={t("customScreenEditor.textColourFor", { n })}
                />
                <TextField
                  size="small"
                  label={t("customScreenEditor.textColour")}
                  value={text.color}
                  onChange={(e) =>
                    onPatch({ text: { ...text, color: e.target.value } })
                  }
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Box>
          </Stack>
        </Section>

        <Section
          title={t("customScreenEditor.qrCode")}
          hint={t("customScreenEditor.qrHint")}
        >
          <Box sx={fieldGrid(280)}>
            <TextField
              size="small"
              label={t("customScreenEditor.address")}
              placeholder={URL_PLACEHOLDER}
              value={qr.url}
              onChange={(e) => onPatch({ qr: { ...qr, url: e.target.value } })}
              data-testid={`custom-qr-url-${index}`}
            />
            <AnchorPicker
              label={t("customScreenEditor.placement")}
              value={qr.placement}
              onChange={(placement) => onPatch({ qr: { ...qr, placement } })}
              testId={`custom-qr-anchor-${index}`}
            />
          </Box>
        </Section>
      </Stack>
    </Box>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography
        variant="bodySm"
        sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
      >
        {title}
      </Typography>
      {hint && (
        <Typography
          variant="meta"
          sx={{ color: tokens.inkFaint, mb: 1, mt: 0.25 }}
        >
          {hint}
        </Typography>
      )}
      <Box sx={{ mt: hint ? 0 : 1 }}>{children}</Box>
    </Box>
  );
}

/**
 * The nine-point placement picker: the grid itself, as buttons.
 *
 * A dropdown listing "middle-left / bottom-centre / …" makes an operator
 * translate words into a picture on every choice. The control IS the screen,
 * so the answer is one look and one click.
 */
function AnchorPicker({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: ScreenAnchor;
  onChange: (next: ScreenAnchor) => void;
  testId?: string;
}) {
  const { t } = useTranslation("templates");
  return (
    <Box>
      <Typography variant="meta" sx={{ color: tokens.inkMuted, mb: 0.75 }}>
        {label}
      </Typography>
      <Box
        data-testid={testId}
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "3px",
          width: 132,
          aspectRatio: "16 / 9",
          p: "3px",
          border: "1px solid",
          borderColor: tokens.divider,
          borderRadius: "8px",
          bgcolor: tokens.rowAlt,
        }}
      >
        {SCREEN_ANCHORS.map((anchor) => (
          <Box
            key={anchor}
            component="button"
            type="button"
            role="radio"
            aria-checked={anchor === value}
            aria-label={t(`customScreenEditor.anchors.${anchor}`)}
            data-testid={`${testId}-${anchor}`}
            onClick={() => onChange(anchor)}
            sx={{
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              p: 0,
              bgcolor: anchor === value ? tokens.brand : tokens.surface,
              opacity: anchor === value ? 1 : 0.65,
              "&:hover": { opacity: 1 },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
